'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Eye, EyeOff, Loader2, ShieldCheck, UserCog, ChevronRight, Lock } from 'lucide-react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type AdminRole = 'super-admin' | 'facilitator' | null;

const SUPER_ADMIN_EMAILS = ['admin@eugenetechrn.io', 'superadmin@eugenetechrn.io', 'eugene@eugenetechrn.io'];
const FACILITATOR_EMAILS = [
  'dr.chen@eugenetechrn.io',
  'j.okafor@eugenetechrn.io',
  'a.mensah@eugenetechrn.io',
  'k.asante@eugenetechrn.io',
  'f.rashid@eugenetechrn.io',
  's.boateng@eugenetechrn.io',
];

function detectAdminRole(email: string): AdminRole {
  const lower = email.toLowerCase().trim();
  if (SUPER_ADMIN_EMAILS.includes(lower)) return 'super-admin';
  if (FACILITATOR_EMAILS.includes(lower)) return 'facilitator';
  return null;
}

const roleConfig = {
  'super-admin': {
    label: 'Super Admin',
    description: 'Full platform access & course management',
    icon: <ShieldCheck size={14} />,
    color: 'text-violet-400',
    bg: 'bg-violet-500/15',
    border: 'border-violet-500/30',
    dot: 'bg-violet-400',
    redirect: '/super-admin',
  },
  facilitator: {
    label: 'Facilitator',
    description: 'Upload resources & track engagement',
    icon: <UserCog size={14} />,
    color: 'text-sky-400',
    bg: 'bg-sky-500/15',
    border: 'border-sky-500/30',
    dot: 'bg-sky-400',
    redirect: '/facilitator-dashboard',
  },
};

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedRole, setDetectedRole] = useState<AdminRole>(null);

  const { signIn } = useAuth();
  const router = useRouter();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError(null);
    setDetectedRole(detectAdminRole(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      return;
    }

    const role = detectAdminRole(email);
    if (!role) {
      setError('Access denied. This portal is for admin staff only.');
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);

      // Verify role from Supabase profile
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        const dbRole = profile?.role;
        if (dbRole === 'super_admin') {
          router.push('/super-admin');
        } else if (dbRole === 'facilitator') {
          router.push('/facilitator-dashboard');
        } else {
          // Fallback: use email-detected role redirect
          router.push(roleConfig[role].redirect);
        }
        router.refresh();
      }
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentRoleCfg = detectedRole ? roleConfig[detectedRole] : null;

  return (
    <div className="min-h-screen flex bg-[#0D0F14]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] flex-shrink-0 bg-[#111318] border-r border-white/5 p-10">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-bold text-xl text-white tracking-tight">EugeneTechRN</span>
        </div>
        <div className="space-y-6">
          <div className="w-12 h-12 rounded-2xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
            <Lock size={22} className="text-violet-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white leading-tight mb-3">
              Admin Portal
            </h1>
            <p className="text-white/50 text-sm leading-relaxed">
              Restricted access for Super Admins and Facilitators only. Your email address determines your role and dashboard.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { icon: <ShieldCheck size={15} className="text-violet-400" />, label: 'Super Admin', desc: 'Full platform control' },
              { icon: <UserCog size={15} className="text-sky-400" />, label: 'Facilitator', desc: 'Resource uploads & tracking' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                {item.icon}
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="text-xs text-white/40">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-white/25">
          Not an admin?{' '}
          <Link href="/sign-up-login-screen" className="text-white/50 hover:text-white transition-colors underline">
            Go to student login
          </Link>
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-white">EugeneTechRN</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/25 mb-4">
              <Lock size={12} className="text-violet-400" />
              <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider">Admin Access Only</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">Staff Sign In</h2>
            <p className="text-white/40 text-sm">Your email determines your role and dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/30 transition-all text-sm"
                placeholder="you@eugenetechrn.io"
                required
              />
            </div>

            {/* Role indicator */}
            {currentRoleCfg && (
              <div className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl border ${currentRoleCfg.bg} ${currentRoleCfg.border}`}>
                <span className={`w-2 h-2 rounded-full ${currentRoleCfg.dot} flex-shrink-0`} />
                <span className={`${currentRoleCfg.color} flex items-center gap-1.5 text-sm font-semibold`}>
                  {currentRoleCfg.icon}
                  {currentRoleCfg.label}
                </span>
                <span className="text-white/40 text-xs flex-1">— {currentRoleCfg.description}</span>
                <ChevronRight size={13} className={currentRoleCfg.color} />
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/30 transition-all text-sm"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Signing in...</>
              ) : (
                <>Sign In{currentRoleCfg ? ` as ${currentRoleCfg.label}` : ''}</>
              )}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}
